import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Customer from "../Modals/Customer";
import OrganizationAxiosInstance from "../Services/OrganizationAxiosInstance";
import Spinner from "../Components/Spinner";
import Error from "../Components/Error";
import { useTranslation } from "react-i18next";
import { FaRegUser } from "react-icons/fa";
import { HiMagnifyingGlass, HiChevronLeft, HiChevronRight } from "react-icons/hi2";

const Customers: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(20);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const { t } = useTranslation();

  useEffect(() => {
    const fetchCustomers = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await OrganizationAxiosInstance.get(`/Customer/All/` + id, {
          params: { page, pageSize, search }
        });
        setCustomers(response.data);
        setTotalCount(parseInt(response.headers["x-total-count"]));
      } catch {
        setError("Failed to load customers");
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, [id, page, pageSize, search]);

  const handleSearch = () => { setSearch(searchInput); setPage(1); };
  const totalPages = Math.ceil(totalCount / pageSize);
  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) setPage(newPage);
  };

  if (loading) return <Spinner size={70} loading={loading} />;
  if (error) return <Error message={error} />;

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="bg-white rounded-2xl border border-brand-100 shadow-sm p-4">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <HiMagnifyingGlass className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder={t("searchPlaceholder")}
              className="w-full ps-9 pe-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 transition flex-shrink-0"
          >
            {t("search")}
          </button>
        </div>
      </div>

      {/* Table card */}
      <div className="bg-white rounded-2xl border border-brand-100 shadow-sm overflow-hidden">
        {/* Mobile cards */}
        <div className="sm:hidden divide-y divide-gray-100">
          {customers.map(customer => (
            <div key={customer.id} className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center flex-shrink-0">
                  <FaRegUser className="w-4 h-4 text-brand-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{customer.name}</p>
                  <p className="text-xs text-gray-500 truncate">{customer.phone}</p>
                </div>
                {customer.active ? (
                  <span className="ms-auto inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700 flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />{t("active")}
                  </span>
                ) : (
                  <span className="ms-auto inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" />{t("inActive")}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">{t("points")}: <strong className="text-gray-900">{customer.pass?.points ?? 0}</strong></span>
                <Link
                  to={`/Dashboard/Program/${id}/Customer/${customer.id}`}
                  className="text-xs px-3 py-1.5 rounded-lg bg-brand-600 text-white font-semibold hover:bg-brand-700 transition"
                >
                  {t("details")}
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <table className="hidden sm:table min-w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="px-6 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wider">{t("name")}</th>
              <th className="px-6 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wider">{t("points")}</th>
              <th className="px-6 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wider">{t("status")}</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {customers.map(customer => (
              <tr key={customer.id} className="hover:bg-brand-50/40 transition">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand-50 flex items-center justify-center flex-shrink-0">
                      <FaRegUser className="w-4 h-4 text-brand-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{customer.name}</p>
                      <p className="text-xs text-gray-500">{customer.phone}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{customer.pass?.points ?? 0}</td>
                <td className="px-6 py-4">
                  {customer.active ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />{t("active")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />{t("inActive")}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-end">
                  <Link
                    to={`/Dashboard/Program/${id}/Customer/${customer.id}`}
                    className="px-3 py-1.5 rounded-lg bg-brand-600 text-white text-xs font-semibold hover:bg-brand-700 transition"
                  >
                    {t("details")}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {customers.length === 0 && (
          <div className="py-16 text-center text-sm text-gray-400">{t("search")}</div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:bg-brand-50 hover:border-brand-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <HiChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600 px-2">
            {t("pageNumber", { from: page, total: totalPages })}
          </span>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:bg-brand-50 hover:border-brand-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <HiChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Customers;
